/* eslint-disable react/jsx-indent */
import {
  Box,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ListingEntry } from 'graphql/listings/queries';
import { ChargeFormEntry } from '../../../types';
import { DescriptionField } from './Fields/DescriptionField';
import { AmountField } from './Fields/AmountField';

const useStyles = makeStyles((theme) => ({
  table: {
    padding: `${theme.spacing(2)}`,
  },
  searchContact: {
    '& .MuiInputLabel-filled': {
      color: theme.palette.primary.dark,
    },
    '& .MuiFilledInput-root': {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.primary.dark,
      border: 'unset',
      borderRadius: '4px',

      '&.MuiFilledInput-underline::before': {
        content: 'unset',
      },

      '&.MuiFilledInput-underline::after': {
        content: 'unset',
      },
    },
  },
  numberField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.primary.dark,
      borderRadius: '5px',
      fontSize: '13px',
      display: 'flex',
      justifyContent: 'flex-end',

      '& .MuiOutlinedInput-notchedOutline': {
        border: 'unset',
      },
      '& .MuiOutlinedInput-input ': {
        textAlign: 'right',
        width: '4rem',
        padding: '8px 6px',
      },
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.background.default,
      borderRadius: '5px',
      color: theme.palette.primary.dark,
      fontSize: '0.8rem',

      '& .MuiOutlinedInput-input': {
        padding: '10px !important',
      },

      '& .MuiOutlinedInput-notchedOutline': {
        border: 'unset',
      },
    },
  },
  numberInput: {
    '& .MuiOutlinedInput-root': {
      width: '35px',
      height: '35px',
      backgroundColor: theme.palette.secondary.main,
      borderRadius: '5px',
      color: theme.palette.primary.dark,

      '& .MuiOutlinedInput-notchedOutline': {
        border: 'unset',
      },
      '& .MuiOutlinedInput-input ': {
        padding: `${theme.spacing(0.25)}`,
        textAlign: 'center',
      },
    },
  },
  numberContainer: {
    width: '30px',
    height: '30px',
    backgroundColor: theme.palette.secondary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '5px',
    color: theme.palette.primary.dark,
  },
  tableChargeRow: {
    padding: `8px ${theme.spacing(0.5)}`,
    fontSize: '0.8rem',
  },
  tableMenuRow: {
    padding: `${theme.spacing(0.25)}`,
    textAlign: 'center',
    cursor: 'pointer',
  },
  options: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: '5px',
  },
  icon: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    fontSize: '0.875rem',
    '&:hover': { color: theme.palette.common.black },
    alignSelf: 'center',
    [theme.breakpoints.down('sm')]: {
      minWidth: theme.spacing(5.5),
      minHeight: theme.spacing(5.5),
    },
  },
  payerIcon: {
    fontSize: theme.typography.body2.fontSize,
  },
}));

interface Props {
  charge: ChargeFormEntry;
  index: number;
  onDelete: (rowIndex: number) => void;
  onEdit: (charge: ChargeFormEntry, rowIndex: number) => void;
  textPredectiveCharges: ListingEntry[];
  hideTax: boolean;
  currentLetter?: string;
  isPartiesInvolvedRowOpen: boolean;
  togglePartiesinvolvedRowOpen: () => void;
  onEditSplitAmount: (charge: ChargeFormEntry, kind: 'Payee' | 'Payer' | 'Tax') => void;
  onChargeAmountChange: (newAmount: number | null) => void;
  onChargePaidByAmountChange: () => void;
}

export const ChargeDocumentRow: FC<Props> = (props) => {
  const {
    charge,
    index,
    onDelete,
    onEdit,
    textPredectiveCharges,
    hideTax,
    currentLetter,
    isPartiesInvolvedRowOpen,
    togglePartiesinvolvedRowOpen,
    onEditSplitAmount,
    onChargeAmountChange,
    onChargePaidByAmountChange,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  const isCharges1200 = currentLetter === '1200';

  return (
    <TableRow>
      {/* Options */}
      <TableCell className={classes.tableChargeRow} colSpan={2}>
        <Box className={classes.options}>
          <IconButton aria-label="expand row" size="small" onClick={togglePartiesinvolvedRowOpen}>
            <FontAwesomeIcon
              icon={isPartiesInvolvedRowOpen ? 'chevron-up' : 'chevron-down'}
              size="sm"
            />
          </IconButton>

          <Tooltip title={t('orders:deleteDocumentTooltip')}>
            <IconButton className={classes.icon} onClick={() => onDelete(index)} size="large">
              <FontAwesomeIcon icon="trash-alt" size="sm" />
            </IconButton>
          </Tooltip>

          <Tooltip title={t('orders:editDocumentTooltip')}>
            <IconButton
              className={classes.icon}
              onClick={() => onEdit(charge, index)}
              size="large"
            >
              <FontAwesomeIcon icon="edit" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>

      {/* Description */}
      <DescriptionField
        isDocument
        isReadOnly
        index={index}
        charge={charge}
        textPredectiveCharges={textPredectiveCharges}
      />

      {/* Payee */}
      <TableCell className={classes.tableChargeRow}>
        <Typography>-------------</Typography>
      </TableCell>
      <TableCell className={classes.tableChargeRow} />

      {/* Amount */}
      <TableCell className={classes.tableChargeRow}>
        <AmountField {...props} name="amount" onAmountChange={onChargeAmountChange} />
      </TableCell>

      {/* Tax */}
      {!hideTax && (
        <TableCell className={classes.tableChargeRow}>
          <Typography style={{ textAlign: 'right' }}>-------</Typography>
        </TableCell>
      )}

      {/* Buyer At */}
      <TableCell className={classes.tableChargeRow}>
        <AmountField {...props} name="buyer_at" onAmountChange={onChargePaidByAmountChange} />
      </TableCell>

      {/* Buyer Before */}
      <TableCell className={classes.tableChargeRow}>
        <AmountField {...props} name="buyer_before" onAmountChange={onChargePaidByAmountChange} />
      </TableCell>

      {isCharges1200 === false && (
        <>
          {/* Selleer At */}
          <TableCell className={classes.tableChargeRow}>
            <AmountField {...props} name="seller_at" onAmountChange={onChargePaidByAmountChange} />
          </TableCell>

          {/* Seller Before */}
          <TableCell className={classes.tableChargeRow}>
            <AmountField
              {...props}
              name="seller_before"
              onAmountChange={onChargePaidByAmountChange}
            />
          </TableCell>

          {/* By Others */}
          <TableCell className={classes.tableChargeRow}>
            <AmountField
              {...props}
              name="by_others"
              onAmountChange={onChargePaidByAmountChange}
            />
          </TableCell>
        </>
      )}

      {/* Select Payers */}
      {isCharges1200 === false && (
        <TableCell className={classes.tableMenuRow}>
          <IconButton
            aria-label={t('orders:payersLabel')}
            className={classes.icon}
            onClick={() => onEditSplitAmount(charge, 'Payer')}
            size="large"
          >
            <FontAwesomeIcon icon="user" className={classes.payerIcon} />
          </IconButton>
        </TableCell>
      )}
    </TableRow>
  );
};
